import { useQueries, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { getAccountDetail } from './api';
import { getFranchiseLocationProfile, getFranchiseProfile } from '../franchise/api';
import { AccountForm } from './AccountForm';

export function AccountEdit() {
  const { id } = useParams<{ id: string }>();

  const detail = useQuery({
    queryKey: ['account', id],
    queryFn: () => getAccountDetail(id!),
    enabled: !!id,
  });

  // The two profile queries only run once the detail query resolves (we need
  // the accountType to decide whether to fetch each). Gated via `enabled`.
  const accountType = detail.data?.account.accountType;
  const profiles = useQueries({
    queries: [
      {
        queryKey: ['franchiseProfile', id],
        queryFn: () => getFranchiseProfile(id!),
        enabled: !!id && accountType === 'brand',
      },
      {
        queryKey: ['franchiseLocationProfile', id],
        queryFn: () => getFranchiseLocationProfile(id!),
        enabled: !!id && accountType === 'location',
      },
    ],
  });

  const profilesLoading =
    (accountType === 'brand' && profiles[0]!.isLoading) ||
    (accountType === 'location' && profiles[1]!.isLoading);

  if (detail.isLoading || profilesLoading) return <CenteredSpinner />;
  if (detail.error || !detail.data) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load account: {(detail.error as Error | undefined)?.message ?? 'not found'}
      </div>
    );
  }

  return (
    <AccountForm
      mode={{
        kind: 'edit',
        account: detail.data.account,
        franchiseProfile: accountType === 'brand' ? (profiles[0]!.data ?? null) : null,
        franchiseLocationProfile:
          accountType === 'location' ? (profiles[1]!.data ?? null) : null,
      }}
    />
  );
}
